import type {
  SiteBuilderBlock,
  SiteBuilderBlockDefinition,
  SiteBuilderDropLine,
  SiteBuilderZoneKind,
} from "@webdo/site-templates/builder";

type BuilderLineDropDefinition = Pick<
  SiteBuilderBlockDefinition,
  "layoutContainer" | "standalone"
>;

export type BuilderStudioLineDropResult = {
  blocks: SiteBuilderBlock[];
  selectedBlockUuid: string;
};

export type BuilderStudioLineDropInput = {
  anchorBlockUuid: string;
  blocks: readonly SiteBuilderBlock[];
  createColumn: () => SiteBuilderBlock;
  dropLine: SiteBuilderDropLine;
  getBlockDefinition: (
    blockType: string,
  ) => BuilderLineDropDefinition | null;
  gridColumns?: number;
  insertedBlockUuid: string;
  parentBlockType?: string | null;
  zoneKind?: SiteBuilderZoneKind | null;
};

const gridSizingOptionKeys = new Set([
  "gridH",
  "gridW",
  "gridX",
  "gridY",
]);

const columnBelowExcludedParentTypes = new Set([
  "accordion",
  "column",
]);

const lineDropExcludedParentTypes = new Set([
  "blockquote",
  "chart",
  "textHighlight",
]);

const columnRestrictedBlockTypes = new Set([
  "blockquote",
  "chart",
  "progressBar",
  "searchBar",
  "share",
  "socialMedia",
  "textHighlight",
]);

function lineDropGroupId(): string {
  return `columns-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function withoutFlowGridSizing(
  block: SiteBuilderBlock,
): SiteBuilderBlock {
  const options = block.options ?? {};
  const nextOptions = Object.fromEntries(
    Object.entries(options).filter(
      ([key]) => !gridSizingOptionKeys.has(key),
    ),
  );
  const nextSlots = block.slots
    ? Object.fromEntries(
        Object.entries(block.slots).map(([slotKey, slotBlocks]) => [
          slotKey,
          slotBlocks.map(withoutFlowGridSizing),
        ]),
      )
    : undefined;
  const nextChildren = block.children?.map(withoutFlowGridSizing);

  return {
    ...block,
    ...(block.options ? { options: nextOptions } : {}),
    ...(nextSlots ? { slots: nextSlots } : {}),
    ...(nextChildren ? { children: nextChildren } : {}),
  };
}

function columnWithContent({
  columnGroup,
  content,
  createColumn,
  inlineLayout = false,
}: {
  columnGroup?: string;
  content: readonly SiteBuilderBlock[];
  createColumn: () => SiteBuilderBlock;
  inlineLayout?: boolean;
}): SiteBuilderBlock {
  const baseColumn = withoutFlowGridSizing(createColumn());

  return {
    ...baseColumn,
    options: {
      ...(baseColumn.options ?? {}),
      alignment: "left",
      builderAutoWrapped: true,
      ...(columnGroup ? { columnGroup } : {}),
      ...(inlineLayout ? { contentLayout: "inlineActions" } : {}),
    },
    slots: {
      ...(baseColumn.slots ?? {}),
      content: content.map(withoutFlowGridSizing),
    },
  };
}

function regularFlowResult({
  anchorBlock,
  anchorIndex,
  blocks,
  createColumn,
  dropLine,
  insertedBlock,
  insertedIndex,
}: {
  anchorBlock: SiteBuilderBlock;
  anchorIndex: number;
  blocks: readonly SiteBuilderBlock[];
  createColumn: () => SiteBuilderBlock;
  dropLine: "below" | "left" | "right";
  insertedBlock: SiteBuilderBlock;
  insertedIndex: number;
}): SiteBuilderBlock[] {
  const insertionIndex = Math.min(anchorIndex, insertedIndex);
  const nextBlocks = blocks.filter(
    (_, index) => index !== anchorIndex && index !== insertedIndex,
  );

  if (dropLine === "left" || dropLine === "right") {
    const groupId = lineDropGroupId();
    const orderedBlocks = dropLine === "left"
      ? [insertedBlock, anchorBlock]
      : [anchorBlock, insertedBlock];
    const columns = orderedBlocks.map((contentBlock) =>
      columnWithContent({
        columnGroup: groupId,
        content: [contentBlock],
        createColumn,
      }),
    );

    nextBlocks.splice(insertionIndex, 0, ...columns);

    return nextBlocks;
  }

  nextBlocks.splice(
    insertionIndex,
    0,
    columnWithContent({
      content: [anchorBlock, insertedBlock],
      createColumn,
    }),
  );

  return nextBlocks;
}

export type BuilderStudioGridLinePlacement = {
  h: number;
  w: number;
  x: number;
  y: number;
};

function positiveGridValue(
  value: unknown,
  fallback: number,
): number {
  const numeric = Number(value);

  return Number.isFinite(numeric)
    ? Math.max(1, Math.round(numeric))
    : fallback;
}

function gridPlacementForBlock(
  block: SiteBuilderBlock,
  gridColumns: number,
): BuilderStudioGridLinePlacement {
  const columns = Math.max(1, Math.round(gridColumns));

  return {
    h: positiveGridValue(block.options?.gridH, 2),
    w: Math.min(
      columns,
      positiveGridValue(
        block.options?.gridW,
        Math.min(6, columns),
      ),
    ),
    x: positiveGridValue(block.options?.gridX, 1),
    y: positiveGridValue(block.options?.gridY, 1),
  };
}

function gridPlacementsOverlap(
  left: BuilderStudioGridLinePlacement,
  right: BuilderStudioGridLinePlacement,
): boolean {
  return !(
    left.x + left.w - 1 < right.x
    || left.x > right.x + right.w - 1
    || left.y + left.h - 1 < right.y
    || left.y > right.y + right.h - 1
  );
}

function gridLineWrapPlacement({
  anchorBlock,
  dropLine,
  gridColumns,
  incomingBlock,
}: {
  anchorBlock: SiteBuilderBlock;
  dropLine: "below" | "left" | "right";
  gridColumns: number;
  incomingBlock: SiteBuilderBlock;
}): BuilderStudioGridLinePlacement | null {
  const columns = Math.max(1, Math.round(gridColumns));
  const anchorPlacement = gridPlacementForBlock(anchorBlock, columns);
  const incomingFootprint = gridPlacementForBlock(incomingBlock, columns);
  const inlineLayout = dropLine === "left" || dropLine === "right";
  const placement = inlineLayout
    ? {
        ...anchorPlacement,
        h: Math.max(anchorPlacement.h, incomingFootprint.h),
        w: anchorPlacement.w + incomingFootprint.w,
        x: dropLine === "left"
          ? Math.max(1, anchorPlacement.x - incomingFootprint.w)
          : anchorPlacement.x,
      }
    : anchorPlacement;

  return (
    placement.x >= 1
    && placement.y >= 1
    && placement.w >= 1
    && placement.h >= 1
    && placement.x + placement.w - 1 <= columns
  )
    ? placement
    : null;
}

/**
 * Resolves the footprint created by a cyan auto-wrap line in a declared grid.
 * The anchor and the moved source are replaced by the wrapper and are ignored
 * for collision checks; every unrelated sibling remains protected.
 */
export function builderStudioAutoWrapGridLinePlacement({
  anchorIndex,
  blocks,
  dropLine,
  gridColumns = 12,
  ignoredIndex = null,
  incomingBlock,
}: {
  anchorIndex: number;
  blocks: readonly SiteBuilderBlock[];
  dropLine: SiteBuilderDropLine;
  gridColumns?: number;
  ignoredIndex?: number | null;
  incomingBlock: SiteBuilderBlock;
}): BuilderStudioGridLinePlacement | null {
  if (
    dropLine !== "left"
    && dropLine !== "right"
    && dropLine !== "below"
  ) {
    return null;
  }

  const anchorBlock = blocks[anchorIndex];

  if (!anchorBlock) {
    return null;
  }

  const placement = gridLineWrapPlacement({
    anchorBlock,
    dropLine,
    gridColumns,
    incomingBlock,
  });

  if (!placement) {
    return null;
  }

  const collision = blocks.some((block, index) => (
    index !== anchorIndex
    && index !== ignoredIndex
    && gridPlacementsOverlap(
      placement,
      gridPlacementForBlock(block, gridColumns),
    )
  ));

  return collision ? null : placement;
}

/**
 * Mirrors the current Shop grid-line wrapper placement. Left/right keeps the
 * anchor's origin (moving it left when needed), grows the wrapper by the
 * incoming footprint and uses an inline-actions content row. Below keeps the
 * anchor placement because the Column owns the vertical content flow.
 */
function gridFlowResult({
  anchorBlock,
  anchorIndex,
  blocks,
  createColumn,
  dropLine,
  gridColumns,
  insertedBlock,
  insertedIndex,
}: {
  anchorBlock: SiteBuilderBlock;
  anchorIndex: number;
  blocks: readonly SiteBuilderBlock[];
  createColumn: () => SiteBuilderBlock;
  dropLine: "below" | "left" | "right";
  gridColumns: number;
  insertedBlock: SiteBuilderBlock;
  insertedIndex: number;
}): SiteBuilderBlock[] | null {
  const inlineLayout = dropLine === "left" || dropLine === "right";
  const layoutPlacement = builderStudioAutoWrapGridLinePlacement({
    anchorIndex,
    blocks,
    dropLine,
    gridColumns,
    ignoredIndex: insertedIndex,
    incomingBlock: insertedBlock,
  });

  if (!layoutPlacement) {
    return null;
  }

  const orderedBlocks = dropLine === "left"
    ? [insertedBlock, anchorBlock]
    : [anchorBlock, insertedBlock];
  const layoutBlock = columnWithContent({
    content: orderedBlocks,
    createColumn,
    inlineLayout,
  });
  const nextBlocks = blocks.filter(
    (_, index) => index !== anchorIndex && index !== insertedIndex,
  );

  nextBlocks.splice(
    Math.min(anchorIndex, insertedIndex),
    0,
    {
      ...layoutBlock,
      options: {
        ...(layoutBlock.options ?? {}),
        gridH: layoutPlacement.h,
        gridW: layoutPlacement.w,
        gridX: layoutPlacement.x,
        gridY: layoutPlacement.y,
      },
    },
  );

  return nextBlocks;
}

/**
 * Materializes Shop-style line drops for the shared editor.
 *
 * The cyan left/right/below guide is therefore not cosmetic: the resulting
 * document contains ordinary, inspectable Column blocks. Module adapters still
 * own their factories and data, while both editors use this neutral operation.
 */
export function builderStudioAutoWrapLineDrop({
  anchorBlockUuid,
  blocks,
  createColumn,
  dropLine,
  getBlockDefinition,
  gridColumns = 12,
  insertedBlockUuid,
  parentBlockType = null,
  zoneKind = null,
}: BuilderStudioLineDropInput): BuilderStudioLineDropResult | null {
  if (
    dropLine !== "left"
    && dropLine !== "right"
    && dropLine !== "below"
  ) {
    return null;
  }

  const anchorIndex = blocks.findIndex(
    (block) => block.uuid === anchorBlockUuid,
  );
  const insertedIndex = blocks.findIndex(
    (block) => block.uuid === insertedBlockUuid,
  );

  if (
    anchorIndex < 0
    || insertedIndex < 0
    || anchorIndex === insertedIndex
  ) {
    return null;
  }

  const anchorBlock = blocks[anchorIndex];
  const insertedBlock = blocks[insertedIndex];
  const anchorDefinition = getBlockDefinition(anchorBlock.type);
  const insertedDefinition = getBlockDefinition(insertedBlock.type);

  if (
    !anchorDefinition?.standalone
    || !insertedDefinition?.standalone
    || anchorDefinition.layoutContainer
    || insertedDefinition.layoutContainer
    || lineDropExcludedParentTypes.has(parentBlockType ?? "")
    || (
      parentBlockType === "column"
      && (
        columnRestrictedBlockTypes.has(anchorBlock.type)
        || columnRestrictedBlockTypes.has(insertedBlock.type)
      )
    )
    || (
      dropLine === "below"
      && columnBelowExcludedParentTypes.has(parentBlockType ?? "")
    )
  ) {
    return null;
  }

  const nextBlocks = zoneKind === "grid"
    ? gridFlowResult({
        anchorBlock,
        anchorIndex,
        blocks,
        createColumn,
        dropLine,
        gridColumns,
        insertedBlock,
        insertedIndex,
      })
    : regularFlowResult({
        anchorBlock,
        anchorIndex,
        blocks,
        createColumn,
        dropLine,
        insertedBlock,
        insertedIndex,
      });

  return nextBlocks
    ? {
        blocks: nextBlocks,
        selectedBlockUuid: insertedBlockUuid,
      }
    : null;
}
